import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tab, Tabs, Typography, AppBar, Box, IconButton, TextField, Modal } from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import { FilterList, Search } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import Page from '../../../../components/Page';
import { getFilterableTradeHistory } from '../../../../redux/slices/trades';
import TradeHistoryRow from './TradeHistoryRow';
import TradeHistoryFilterCard from '../../../../pages/dashboard/operator/TradeHistoryFilterCard';

// eslint-disable-next-line react/prop-types
const HeaderItem = ({ label, value }) => (
  <Box
    display="flex"
    flexDirection={{ xs: 'row', sm: 'row' }}
    justifyContent={'space-between'}
    flexGrow={1}
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    sx={{
      padding: 1.5,
      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
      borderRadius: '8px',
      marginBottom: { xs: 1, sm: 0 },
      backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
      boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.03)}`,
      transition: 'all 0.2s',
      '&:hover': {
        boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.common.black, 0.05)}`,
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
      },
    }}
  >
    <Typography variant="body2" color={(theme) => alpha(theme.palette.text.primary, 0.6)} fontWeight={500}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} color="primary.main">
      {value}
    </Typography>
  </Box>
);

const TradeHistory = () => {
  const dispatch = useDispatch();
  const { ftradeHistory, fbookedPl, fbrokerage } = useSelector((state) => state?.trade);
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState(''); // New state for search term
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false); // Popup state

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSwipeChangeIndex = (index) => {
    setTabValue(index);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(getFilterableTradeHistory());
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tabValue]);

  const handleSearchIconClick = () => {
    setShowSearchBar((prev) => !prev);
  };

  const handleFilterIconClick = () => {
    setShowFilterPopup(true);
  };

  const handleClosePopup = () => {
    setShowFilterPopup(false);
  };

  // Filter trade history based on the selected tab and search term
  const filteredTradeHistory = ftradeHistory.filter((trade) => {
    // Filter based on the selected tab and search term
    const matchesTab =
      (tabValue === 0 && trade.type === 'ORDER') ||
      (tabValue === 1 && trade.type === 'LIMIT') ||
      (tabValue === 2 && (trade.type === 'CLOSE' || trade.type === 'OPEN')) ||
      (tabValue !== 0 && tabValue === 1 && tabValue !== 2 && ['EXECUTED', 'CANCELLED'].includes(trade.type));

    const matchesSearch = searchTerm
      ? trade?.script?.tradingsymbol && trade?.script?.tradingsymbol.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    return matchesTab && matchesSearch;
  });

  return (
    <Page title="Portfolio" sx={{ px: 0.5, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.primary.dark, 0.1)}`,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.95)
              : alpha(theme.palette.background.paper, 0.95),
          borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          px: 0,
          height: 56,
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 56,
            px: { xs: 1, sm: 2 },
            [theme.breakpoints.down('md')]: {
              gap: 0.25,
            },
          }}
        >
          <IconButton
            onClick={handleFilterIconClick}
            sx={{
              color: 'primary.main',
              width: 40,
              height: 40,
              mx: { xs: 0.2, sm: 0.5 },
              transition: 'all 0.15s',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                transform: 'translateY(-1px)',
              },
            }}
          >
            <FilterList sx={{ fontSize: '1.4rem' }} />
          </IconButton>
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            indicatorColor="primary"
            textColor="primary"
            centered
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                fontWeight: 600,
                minWidth: 'auto',
                minHeight: 40,
                px: { xs: 1, sm: 2 },
                transition: 'all 0.15s',
                color: (theme) => alpha(theme.palette.text.primary, 0.7),
                letterSpacing: '0.5px',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
                [theme.breakpoints.down('md')]: {
                  fontSize: '0.7rem',
                  px: 1,
                },
                '&:hover': {
                  color: 'primary.main',
                  opacity: 0.85,
                },
              },
              '& .MuiTabs-indicator': {
                height: 2,
                borderRadius: 1,
                transition: 'all 0.15s',
              },
            }}
          >
            <Tab label="TRADES" value={0} />
            <Tab label="LIMITS" value={1} />
            <Tab label="DEALS" value={2} />
          </Tabs>
          <IconButton
            onClick={handleSearchIconClick}
            sx={{
              color: 'primary.main',
              width: 40,
              height: 40,
              mx: { xs: 0.2, sm: 0.5 },
              transition: 'all 0.15s',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Search sx={{ fontSize: '1.4rem' }} />
          </IconButton>
        </Box>
      </AppBar>

      <Modal
        open={showFilterPopup}
        onClose={handleClosePopup}
        aria-labelledby="filter-popup-title"
        aria-describedby="filter-popup-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: { xs: '90%', sm: '60%', md: '40%' },
          }}
        >
          <TradeHistoryFilterCard onClose={handleClosePopup} />
        </Box>
      </Modal>
      {showSearchBar && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 1.5,
            px: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.default, 0.97)
                : alpha(theme.palette.background.paper, 0.97),
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            position: 'fixed',
            top: 56,
            left: 0,
            right: 0,
            zIndex: 1099,
            borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
          }}
        >
          <TextField
            placeholder="Search Script"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search color="action" sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.7 }} />,
            }}
            sx={{
              maxWidth: 500,
              '& .MuiOutlinedInput-root': {
                height: 40,
                borderRadius: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.7)
                    : alpha(theme.palette.background.default, 0.7),
                '& fieldset': {
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
                '& input': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  paddingTop: '8px',
                  paddingBottom: '8px',
                },
              },
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          mt: showSearchBar ? 13 : 5,
          pb: 2
        }}
      >
        <SwipeableViews
          index={tabValue}
          onChangeIndex={handleSwipeChangeIndex}
          containerStyle={{ minHeight: '100%' }}
          style={{ height: '100%' }}
        >
          <Box role="tabpanel" hidden={tabValue !== 0}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)' }}>
              {filteredTradeHistory.length > 0 ? (
                <>
                  {filteredTradeHistory.map((trade) => (
                    <TradeHistoryRow trade={trade} key={trade._id} isDealsTab={tabValue === 2} />
                  ))}

                  {!showSearchBar && (<Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                      width: '100%',
                      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                      padding: { xs: 1.5, sm: 2, md: 2.5 },
                      borderRadius: '12px',
                      gap: { xs: 1.5, sm: 2, md: 3 },
                      backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
                      boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                      mt: 2,
                      mb: 2
                    }}
                  >
                    <HeaderItem label={'BOOKED P/L'} value={fbookedPl?.toFixed(2)} />
                    <HeaderItem label={'BROKERAGE'} value={fbrokerage?.toFixed(2)} />
                    <HeaderItem label={'GROSS P/L'} value={(fbookedPl - fbrokerage)?.toFixed(2)} />
                  </Box>
                  )}
                </>
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    textAlign: 'center',
                    paddingTop: 8,
                    color: (theme) => alpha(theme.palette.text.primary, 0.6),
                    fontWeight: 500,
                  }}
                >
                  NO TRADES
                </Typography>
              )}
            </Box>
          </Box>

          <Box role="tabpanel" hidden={tabValue !== 1}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)' }}>
              {filteredTradeHistory.length > 0 ? (
                filteredTradeHistory.map((trade) => <TradeHistoryRow trade={trade} key={trade._id} isDealsTab={tabValue === 2} />)
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    textAlign: 'center',
                    paddingTop: 8,
                    color: (theme) => alpha(theme.palette.text.primary, 0.6),
                    fontWeight: 500,
                  }}
                >
                  NO LIMITS
                </Typography>
              )}
            </Box>
          </Box>

          <Box role="tabpanel" hidden={tabValue !== 2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)' }}>
              {filteredTradeHistory.length > 0 ? (
                filteredTradeHistory.map((trade) => <TradeHistoryRow trade={trade} key={trade._id} isDealsTab={tabValue === 2} />)
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    textAlign: 'center',
                    paddingTop: 8,
                    color: (theme) => alpha(theme.palette.text.primary, 0.6),
                    fontWeight: 500,
                  }}
                >
                  NO DEALS
                </Typography>
              )}
            </Box>
          </Box>
        </SwipeableViews>
      </Box>
    </Page>
  );
};

export default TradeHistory;
